import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { snapshotsEqual } from './GlobalFilterController';
import { GLOBAL_FILTER_PARAM_KEYS, decodeGlobalFilters, encodeGlobalFilters } from './GlobalFilterUrlCodec';
import { useGlobalFilterController } from './useGlobalFilters';

/**
 * Two-way sync between the URL query string and the {@link GlobalFilterController}.
 * The controller is the in-memory source of truth and persists across navigation
 * (a root singleton); the URL mirrors it so filters also survive reload and
 * deep-links (CloudSaver 3.0 style, no localStorage). Mount once, high in the
 * tree and inside the router (the app shell).
 *
 * In-app navigation drops the query string (`navigate('/x')`), so we must NOT
 * treat a bare URL as "filters cleared": the URL is only hydrated into the store
 * when it actually carries global params (deep-link / back-forward), and the
 * store is re-stamped into the URL after every navigation so the params reappear.
 * Only the codec-owned params are touched; others are preserved.
 */
export function useGlobalFilterUrlSync(): void {
    const controller = useGlobalFilterController();
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const paramsRef = useRef(params);
    paramsRef.current = params;
    const hydratedRef = useRef(false);

    if (!hydratedRef.current) {
        controller.hydrate(decodeGlobalFilters(params));
        hydratedRef.current = true;
    }

    const writeIfDrifted = useCallback((): void => {
        const snapshot = controller.snapshot();
        if (snapshotsEqual(decodeGlobalFilters(paramsRef.current), snapshot)) return;
        setParams((prev) => encodeGlobalFilters(snapshot, prev), { replace: true });
    }, [controller, setParams]);

    useEffect(() => {
        if (!hasGlobalParams(params)) return;
        const fromUrl = decodeGlobalFilters(params);
        if (!snapshotsEqual(fromUrl, controller.snapshot())) controller.hydrate(fromUrl);
    }, [controller, params]);

    useEffect(() => {
        writeIfDrifted();
    }, [location.pathname, writeIfDrifted]);

    useEffect(() => controller.subscribe(writeIfDrifted), [controller, writeIfDrifted]);
}

function hasGlobalParams(params: URLSearchParams): boolean {
    return GLOBAL_FILTER_PARAM_KEYS.some((key) => params.has(key));
}
